import React, { useState, useEffect } from 'react';
import { X, Settings } from 'lucide-react';
import { initializeAnalytics } from '../Config/firebase';

const CookiePreferencesModal = ({ isOpen, onClose, preferences, setPreferences }) => {
  if (!isOpen) return null;

  const handleSavePreferences = () => {
    setPreferences(preferences);
    initializeAnalytics(preferences);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg p-4 sm:p-6 max-w-2xl w-full">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-medium text-gray-800 font-trends">Cookie Preferences</h2>
          <button 
            onClick={onClose} 
            className="p-1 text-gray-400 hover:text-gray-600 transition duration-200"
          >
            <X size={24} />
          </button>
        </div>
        
        <div className="space-y-4">
          {/* Essential Cookies */}
          <div className="flex items-center justify-between p-4 border rounded bg-gray-50">
            <div className="flex-1 pr-4">
              <h3 className="font-medium text-gray-800 mb-1">Essential Cookies</h3>
              <p className="text-sm text-gray-600">Required for the website to function properly. These cannot be disabled.</p>
            </div>
            <div className="flex items-center whitespace-nowrap">
              <span className="text-sm text-gray-500 mr-2">Always active</span>
              <input 
                type="checkbox" 
                checked={true} 
                disabled 
                className="h-4 w-4 accent-[#E57373]"
              />
            </div>
          </div>

          {/* Analytics Cookies */}
          <div className="flex items-center justify-between p-4 border rounded hover:bg-gray-50 transition duration-200">
            <div className="flex-1 pr-4">
              <h3 className="font-medium text-gray-800 mb-1">Analytics Cookies</h3>
              <p className="text-sm text-gray-600">Help us understand how visitors interact with our website</p>
            </div>
            <input 
              type="checkbox" 
              checked={preferences.analytics} 
              onChange={(e) => setPreferences(prev => ({...prev, analytics: e.target.checked}))}
              className="h-4 w-4 accent-[#E57373] cursor-pointer"
            />
          </div>

          {/* Marketing Cookies */}
          <div className="flex items-center justify-between p-4 border rounded hover:bg-gray-50 transition duration-200">
            <div className="flex-1 pr-4">
              <h3 className="font-medium text-gray-800 mb-1">Marketing Cookies</h3>
              <p className="text-sm text-gray-600">Used to deliver personalized advertisements</p>
            </div>
            <input 
              type="checkbox" 
              checked={preferences.marketing} 
              onChange={(e) => setPreferences(prev => ({...prev, marketing: e.target.checked}))}
              className="h-4 w-4 accent-[#E57373] cursor-pointer"
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={handleSavePreferences}
            className="px-8 py-2.5 bg-[#E57373] hover:bg-[#EF9A9A] text-white rounded-full transition duration-200"
          >
            Save Preferences
          </button>
        </div>
      </div>
    </div>
  );
};

const CookieConsent = ({ children }) => {
  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true,
    analytics: false,
    marketing: false
  });
  
  useEffect(() => {
    const consentStatus = localStorage.getItem('cookieConsent');
    if (consentStatus) {
      try {
        const savedPreferences = JSON.parse(consentStatus);
        setPreferences(savedPreferences);
        initializeAnalytics(savedPreferences);
      } catch (e) {
        setShowBanner(true);
      }
    } else {
      setShowBanner(true);
    }
  }, []);

  const handleAccept = () => {
    const newPreferences = {
      essential: true,
      analytics: true,
      marketing: true
    };
    localStorage.setItem('cookieConsent', JSON.stringify(newPreferences));
    setPreferences(newPreferences);
    initializeAnalytics(newPreferences);
    setShowBanner(false);
  };

  const handleDecline = () => {
    const newPreferences = {
      essential: true,
      analytics: false,
      marketing: false
    };
    localStorage.setItem('cookieConsent', JSON.stringify(newPreferences));
    setPreferences(newPreferences);
    initializeAnalytics(newPreferences);
    setShowBanner(false);
  };

  const handlePreferencesUpdate = (newPrefs) => {
    setPreferences(newPrefs);
    localStorage.setItem('cookieConsent', JSON.stringify(newPrefs));
    initializeAnalytics(newPrefs);
  };

  return (
    <div className="relative">
      {children}
      
      {showBanner && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50">
          <div className="max-w-6xl mx-auto px-4 py-4 sm:py-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex-1">
                <p className="text-gray-700 text-sm sm:text-base text-center sm:text-left">
                  We use cookies to enhance your browsing experience and analyze our traffic. 
                  You can customize your preferences or accept all cookies.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-3">
                <button
                  onClick={() => setShowModal(true)}
                  className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition duration-200"
                >
                  <Settings size={20} />
                  <span>Preferences</span>
                </button>
                <button
                  onClick={handleDecline}
                  className="w-full sm:w-auto px-6 py-2 text-gray-600 border border-gray-300 rounded-full hover:bg-gray-100 transition duration-200"
                >
                  Essential Only
                </button>
                <button
                  onClick={handleAccept}
                  className="w-full sm:w-auto px-6 py-2 bg-[#E57373] hover:bg-[#EF9A9A] text-white rounded-full transition duration-200"
                >
                  Accept All
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <CookiePreferencesModal 
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        preferences={preferences}
        setPreferences={handlePreferencesUpdate}
      />
    </div>
  );
};

export default CookieConsent;